import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useCopy } from "../../hooks/useCopy";
import { useMoney } from "../../hooks/useMoney";
import { ProdAttrUtils } from "../../utils/productAttributeUtils";
import LikeBtn from "../detail/LikeBtn";
import { UiTitle } from "../text/UiTitle";
import BlackToast from "../toast/BlackToast";
import ProductSummary from "./ProductSummary";
import { RichTextBox } from "../richtext/RichTextBox";
import { icAsset } from "../image/image-src";

interface ProductInfoHeaderProps {
  tagging?: string;
  d?: ProductDetailResponse;
}

const useLs = ({ d }: ProductInfoHeaderProps) => {
  const calcDisCount = (
    discount?: number,
    discountType?: string,
    price?: number,
  ) => {
    if (!discount || !price) return undefined;
    if (discountType === "PERCENT") {
      return price - (price * discount) / 100;
    }
    if (discountType === "FIXED") {
      return price - discount;
    }
  };
  const { t } = useTranslation();
  if (d?.productType === "TOUR" || d?.productType === "TICKET") {
    const minAdultPrice = ProdAttrUtils.findMinPrice("ADULT", d?.attributeList);
    const minChildPrice = ProdAttrUtils.findMinChildPrice(
      "CHILD",
      d?.attributeList,
    );
    return [
      <TtKv
        key={1}
        k={t("Adult")}
        originalPrice={minAdultPrice}
        discountedPrice={calcDisCount(
          d.discount?.value,
          d.discount?.discountType,
          minAdultPrice,
        )}
      />,
      <TtKv
        key={2}
        k={t("Child")}
        originalPrice={minChildPrice}
        discountedPrice={calcDisCount(
          d.discount?.childValue,
          d.discount?.discountType,
          minChildPrice,
        )}
      />,
      <TtKv key={3} k={t(d?.cityName ?? "")} />,
    ].filter(
      (_, i) =>
        [
          !!minAdultPrice,
          minChildPrice !== null &&
            minChildPrice !== undefined &&
            !isNaN(minChildPrice),
          !!d?.cityName,
        ][i],
    );
  }
  if (d?.productType === "MUSICAL") {
    return [
      <TtKv
        key={1}
        k={t("Price")}
        originalPrice={d.price}
        displayPriceRank={d.displayPriceType === "RANGE"}
      />,
      <TtKv key={2} k={t(d?.cityName ?? "")} />,
    ].filter((_, i) => [!!d.price, !!d?.cityName][i]);
  }
  return [];
};
const ProductInfoHeader: React.FC<ProductInfoHeaderProps> = (props) => {
  const { t } = useTranslation();

  const { d } = props;
  const tagging =
    d?.productType === "MUSICAL" || d?.productType === "SIM"
      ? ""
      : d?.productType === "PASS"
        ? d?.cityName
        : d?.categoryName;
  const ls = useLs(props);
  const { copySuccess, handleCopy } = useCopy();

  return (
    <>
      {tagging && (
        <p className="">
          <span className="rounded-[0.3125rem] bg-[#F7F8FA] px-[0.5rem] py-[0.5625rem] text-[0.75rem] font-normal leading-[1.125rem] text-[#5E6D77]">
            {t(tagging)}
          </span>
        </p>
      )}

      {d?.name && <UiTitle className={"my-2"} text={t(d?.name)} />}

      {ls.length > 0 && (
        <div className="mb-[2.5rem] flex items-center gap-[0.75rem]">
          {ls.map((x, i, a) => (
            <>
              {x}
              {i < a.length - 1 && <TtDot />}
            </>
          ))}
        </div>
      )}
      <div className="mb-[2.5rem] hidden gap-[0.75rem] sm:flex">
        <button
          onClick={() => handleCopy()}
          className="h-[2.75rem] w-[2.75rem] content-center rounded-full border border-[#DEDEDE] text-center"
        >
          <img
            className="mx-auto h-4 w-4"
            src={icAsset("../../assets/icons/share.svg")}
            alt="share"
          />
        </button>
        <LikeBtn id={d?.id} />
      </div>
      <ProductSummary d={d} />
      {d?.productType === "PASS" && (
        <div className="pd-left w-full">
          {d?.intro && <RichTextBox richtext={d?.intro} />}
          <div
            onClick={() => {
              window.open("/pass/introduction", "_blank");
            }}
            className="mb-10 mt-4 inline-flex cursor-pointer items-center font-medium underline"
          >
            {t("View Big pass introduction")}
            <img
              src={icAsset("../../../assets/icons/next-arrow-black.svg")}
              alt=""
              className="ml-2"
            />
          </div>
        </div>
      )}
      <BlackToast message={copySuccess} />
    </>
  );
};

export default ProductInfoHeader;

const TtDot = () => (
  <div className="h-[0.375rem] w-[0.375rem] rounded-full bg-[#EAEEF3]"></div>
);
const TtKv: FC<{
  k: string;
  originalPrice?: number;
  discountedPrice?: number;
  displayPriceRank?: boolean;
}> = ({ k, originalPrice, discountedPrice, displayPriceRank }) => {
  const { formatMoney } = useMoney();
  return (
    <p className="text-base font-semibold leading-[1.5rem] text-[#232323]">
      {k}
      {originalPrice !== null &&
        originalPrice !== undefined &&
        !isNaN(originalPrice) && (
          <span className="ml-[0.5rem] text-base font-normal leading-[1.5rem] text-[#5E6D77]">
            {formatMoney(discountedPrice || originalPrice)}
            {displayPriceRank && "~"}
          </span>
        )}
      {!!discountedPrice && (
        <span className="ml-[0.5rem] text-sm font-normal text-[#BFC5C9] line-through">
          {formatMoney(originalPrice || discountedPrice)}
          {displayPriceRank && "~"}
        </span>
      )}
    </p>
  );
};
